import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Info, CalendarRange, X } from 'lucide-react';
import { formatDateShort } from '../../utils/dateUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAY_NAMES = ['L','M','M','J','V','S','D'];

// ── STATUTS VERT / ORANGE / ROUGE ─────────────────────────────────────────
const STATUS_CONFIG = {
  disponible:       { bg: '#DCFCE7', border: '#22C55E', text: '#15803D', dot: '#22C55E', label: 'Disponible' },
  en_demande:       { bg: '#FEF9C3', border: '#F59E0B', text: '#92400E', dot: '#F59E0B', label: 'En demande' },
  controle_requis:  { bg: '#FFF7ED', border: '#F97316', text: '#C2410C', dot: '#F97316', label: 'Sous contrôle' },
  reserve:          { bg: '#FEE2E2', border: '#EF4444', text: '#991B1B', dot: '#EF4444', label: 'Réservé' },
  indisponible:     { bg: '#F3F4F6', border: '#9CA3AF', text: '#4B5563', dot: '#9CA3AF', label: 'Indisponible' },
};

const LEGACY_STATUS = {
  reserve: 'reserve',
  bloque: 'indisponible',
  preparation: 'controle_requis',
  en_attente: 'en_demande',
};

// ── UTILITAIRES DATE ───────────────────────────────────────────────────────
function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isBetween(dateStr, startStr, endStr) {
  if (!startStr || !endStr) return false;
  return dateStr > startStr && dateStr < endStr;
}

function countDays(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  const start = new Date(startStr);
  const end = new Date(endStr);
  return Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
}

function datesInRange(startStr, endStr) {
  const dates = [];
  if (!startStr || !endStr) return dates;
  const start = new Date(startStr);
  const end = new Date(endStr);
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(toISO(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

/**
 * SmartAvailabilityCalendar v2 — Sélection plage de dates
 * Props:
 *   productId        : ID produit
 *   onRangeSelect    : (startDate, endDate, nbJours, hasConflict) => void
 *   selectedStartDate: string 'YYYY-MM-DD' (contrôlé par parent)
 *   selectedEndDate  : string 'YYYY-MM-DD' (contrôlé par parent)
 *   onDateSelect     : (date) => void  [compat rétroactive]
 */
export default function SmartAvailabilityCalendar({
  productId,
  onRangeSelect,
  onDateSelect,
  selectedStartDate,
  selectedEndDate,
}) {
  const now = new Date();
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [annee, setAnnee] = useState(now.getFullYear());
  const [calendarMap, setCalendarMap] = useState({});
  const [nextAvailable, setNextAvailable] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  // État interne de la sélection (hover pour preview)
  const [hoverDate, setHoverDate] = useState(null);

  const fetchCalendar = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const d_from = `${annee}-${String(mois).padStart(2, '0')}-01`;
      const lastDay = new Date(annee, mois, 0).getDate();
      const d_to = `${annee}-${String(mois).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const res = await fetch(`${BACKEND}/api/disponibilites/produit/${productId}?from_date=${d_from}&to_date=${d_to}`);
      if (res.ok) {
        const data = await res.json();
        setCalendarMap(prev => ({ ...prev, ...data.calendar }));
        setNextAvailable(data.next_available);
      } else {
        const res2 = await fetch(`${BACKEND}/api/availability/product/${productId}?mois=${mois}&annee=${annee}&nb_mois=1`);
        const data2 = await res2.json();
        const map = {};
        (data2.calendrier || []).forEach(d => {
          const legacy = LEGACY_STATUS[d.statut] || (d.disponible ? 'disponible' : 'indisponible');
          map[d.date] = legacy;
        });
        setCalendarMap(prev => ({ ...prev, ...map }));
      }
    } catch { }
    setLoading(false);
  }, [productId, mois, annee]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  // Construire la grille du mois affiché
  function buildGrid() {
    const firstDay = new Date(annee, mois - 1, 1);
    const startDay = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(annee, mois, 0).getDate();
    const today = toISO(new Date());

    const grid = [];
    for (let i = 0; i < startDay; i++) grid.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${annee}-${String(mois).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const status = calendarMap[dateStr] || 'disponible';
      grid.push({ date: dateStr, day: d, status, isPast: dateStr < today });
    }
    return grid;
  }

  // Vérifier s'il y a des conflits dans la plage affichée
  function checkConflictInRange(start, end) {
    if (!start || !end) return false;
    const range = datesInRange(start, end);
    return range.some(d => {
      const st = calendarMap[d] || 'disponible';
      return st === 'reserve' || st === 'indisponible';
    });
  }

  function handleClick(cell) {
    if (!cell || cell.isPast) return;
    if (cell.status === 'reserve' || cell.status === 'indisponible') return;

    const clickedDate = cell.date;

    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Cas 1 : pas de sélection en cours → on commence une nouvelle
      if (onRangeSelect) onRangeSelect(clickedDate, null, 1, false);
      if (onDateSelect) onDateSelect(clickedDate);
      return;
    }

    if (selectedStartDate && !selectedEndDate) {
      if (clickedDate === selectedStartDate) {
        // Annuler la sélection
        if (onRangeSelect) onRangeSelect(null, null, 0, false);
        if (onDateSelect) onDateSelect(null);
        return;
      }

      let start = selectedStartDate;
      let end = clickedDate;
      if (end < start) { [start, end] = [end, start]; }

      const conflict = checkConflictInRange(start, end);
      const nb = countDays(start, end);

      if (onRangeSelect) onRangeSelect(start, end, nb, conflict);
      if (onDateSelect) onDateSelect(start);
      setHoverDate(null);
      return;
    }
  }

  function handleMouseEnter(cell) {
    if (!cell || cell.isPast) return;
    if (selectedStartDate && !selectedEndDate) {
      setHoverDate(cell.date);
    }
  }

  function handleMouseLeave() {
    setHoverDate(null);
  }

  const grid = buildGrid();

  // Plage de preview (avant validation du 2ème clic)
  const previewEnd = selectedStartDate && !selectedEndDate ? hoverDate : null;
  const previewStart = previewEnd ? (previewEnd < selectedStartDate ? previewEnd : selectedStartDate) : null;
  const previewEndNorm = previewEnd ? (previewEnd < selectedStartDate ? selectedStartDate : previewEnd) : null;

  const prevMonth = () => {
    const today = toISO(new Date());
    const firstOfMonth = `${annee}-${String(mois).padStart(2, '0')}-01`;
    if (firstOfMonth <= today) return; // Ne pas aller dans le passé
    if (mois === 1) { setMois(12); setAnnee(a => a - 1); }
    else setMois(m => m - 1);
  };
  const nextMonth = () => {
    if (mois === 12) { setMois(1); setAnnee(a => a + 1); }
    else setMois(m => m + 1);
  };

  const nbJoursSelectionnes = selectedStartDate && selectedEndDate
    ? countDays(selectedStartDate, selectedEndDate)
    : selectedStartDate ? 1 : 0;

  const conflictInCurrentRange = selectedStartDate && selectedEndDate
    ? checkConflictInRange(selectedStartDate, selectedEndDate)
    : false;

  return (
    <div data-testid="availability-calendar" className="rounded-2xl overflow-hidden"
      style={{ border: '1px solid var(--border)', background: '#FFFFFF' }}>

      {/* ── Titre mode sélection plage ── */}
      <div className="px-4 pt-3 pb-2 flex items-center justify-between"
        style={{ borderBottom: '1px solid var(--border)', background: '#FAFAFA' }}>
        <div className="flex items-center gap-2">
          <CalendarRange size={15} style={{ color: 'var(--teal)' }} />
          <p className="text-xs font-bold" style={{ color: 'var(--ink)' }}>
            {!selectedStartDate && 'Sélectionnez une date de début'}
            {selectedStartDate && !selectedEndDate && 'Sélectionnez la date de fin'}
            {selectedStartDate && selectedEndDate && (
              <span style={{ color: '#1F9D7A' }}>
                {nbJoursSelectionnes} jour{nbJoursSelectionnes > 1 ? 's' : ''} sélectionné{nbJoursSelectionnes > 1 ? 's' : ''}
              </span>
            )}
          </p>
        </div>
        {selectedStartDate && (
          <button
            onClick={() => { if (onRangeSelect) onRangeSelect(null, null, 0, false); if (onDateSelect) onDateSelect(null); }}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
            style={{ background: 'var(--border)', color: 'var(--text-muted)', border: 'none', cursor: 'pointer' }}>
            <X size={11} /> Réinitialiser
          </button>
        )}
      </div>

      {/* ── Navigation mois ── */}
      <div className="flex items-center justify-between px-4 py-2.5"
        style={{ borderBottom: '1px solid var(--border)' }}>
        <button onClick={prevMonth}
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', cursor: 'pointer' }}>
          <ChevronLeft size={14} />
        </button>
        <div className="text-center">
          <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>
            {MONTH_NAMES[mois - 1]} {annee}
          </p>
          {nextAvailable && !selectedStartDate && (
            <p className="text-xs" style={{ color: '#22C55E' }}>
              Prochaine dispo : {formatDateShort(nextAvailable)}
            </p>
          )}
        </div>
        <button onClick={nextMonth}
          className="w-8 h-8 rounded-xl flex items-center justify-center"
          style={{ background: 'var(--bg)', border: '1px solid var(--border)', cursor: 'pointer' }}>
          <ChevronRight size={14} />
        </button>
      </div>

      {/* Jours de la semaine */}
      <div className="grid grid-cols-7 px-2 pt-2">
        {DAY_NAMES.map((d, i) => (
          <div key={i} className="text-center text-xs font-bold py-1"
            style={{ color: i >= 5 ? '#5DBCBF' : 'var(--text-muted)' }}>
            {d}
          </div>
        ))}
      </div>

      {/* ── Grille calendrier ── */}
      <div className="grid grid-cols-7 gap-0.5 px-2 pb-2" onMouseLeave={handleMouseLeave}>
        {loading ? (
          Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-9 rounded-lg animate-pulse" style={{ background: 'var(--border)' }} />
          ))
        ) : (
          grid.map((cell, i) => {
            if (!cell) return <div key={i} />;
            const conf = STATUS_CONFIG[cell.status] || STATUS_CONFIG.disponible;
            const isStart = selectedStartDate === cell.date;
            const isEnd = selectedEndDate === cell.date;
            const isInRange = isBetween(cell.date, selectedStartDate, selectedEndDate);
            const isPreviewRange = previewStart && previewEndNorm
              ? isBetween(cell.date, previewStart, previewEndNorm) || cell.date === previewStart || cell.date === previewEndNorm
              : false;
            const isClickable = !cell.isPast && cell.status !== 'reserve' && cell.status !== 'indisponible';
            const isSelected = isStart || isEnd;

            // Couleurs
            let bgColor = cell.isPast ? 'transparent' : conf.bg;
            let borderColor = cell.isPast ? 'transparent' : conf.border;
            let textColor = cell.isPast ? '#D1D5DB' : conf.text;
            let borderRadius = '0.5rem';

            if (isSelected) {
              bgColor = '#0B1E20';
              borderColor = '#D4AF37';
              textColor = '#D4AF37';
            } else if (isInRange) {
              bgColor = 'rgba(93,188,191,0.15)';
              borderColor = 'rgba(93,188,191,0.4)';
              textColor = '#0B4B4E';
              borderRadius = '0';
            } else if (isPreviewRange && !isSelected) {
              bgColor = 'rgba(93,188,191,0.08)';
              borderColor = 'rgba(93,188,191,0.25)';
              textColor = '#0B4B4E';
            }

            // Arrondi uniquement sur les extrémités de plage
            if (isStart && selectedEndDate) borderRadius = '0.5rem 0 0 0.5rem';
            if (isEnd && selectedStartDate) borderRadius = '0 0.5rem 0.5rem 0';
            if (isStart && isEnd) borderRadius = '0.5rem';

            return (
              <button
                key={i}
                onClick={() => handleClick(cell)}
                onMouseEnter={() => handleMouseEnter(cell)}
                disabled={!isClickable}
                title={conf.label}
                data-testid={`cal-day-${cell.date}`}
                className="h-9 w-full text-xs font-semibold flex items-center justify-center transition-all relative"
                style={{
                  background: bgColor,
                  border: `1px solid ${borderColor}`,
                  color: textColor,
                  cursor: isClickable ? 'pointer' : cell.isPast ? 'default' : 'not-allowed',
                  opacity: cell.isPast ? 0.35 : 1,
                  borderRadius,
                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                }}>
                {cell.day}
                {!cell.isPast && cell.status !== 'disponible' && (
                  <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full"
                    style={{ background: conf.dot }} />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* ── Alerte conflit ── */}
      {conflictInCurrentRange && (
        <div className="mx-3 mb-2 p-2.5 rounded-xl flex items-start gap-2"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
          <span className="text-xs font-semibold" style={{ color: '#DC2626' }}>
            ⚠️ Cette plage contient des dates réservées ou indisponibles. Nous vérifierons la disponibilité lors de votre demande.
          </span>
        </div>
      )}

      {/* ── Instruction ── */}
      {!selectedStartDate && (
        <p className="text-xs text-center pb-3" style={{ color: 'var(--text-muted)' }}>
          👆 Cliquez sur une date <span style={{ color: '#22C55E', fontWeight: 600 }}>disponible</span> pour commencer
        </p>
      )}
      {selectedStartDate && !selectedEndDate && (
        <p className="text-xs text-center pb-3" style={{ color: 'var(--text-muted)' }}>
          📅 Cliquez sur la date de <span style={{ color: 'var(--teal)', fontWeight: 600 }}>fin</span> (ou cliquez à nouveau pour une seule journée)
        </p>
      )}

      {/* ── Légende ── */}
      <div className="px-3 pb-3 pt-1" style={{ borderTop: '1px solid var(--border)' }}>
        <button
          onClick={() => setShowLegend(!showLegend)}
          className="flex items-center gap-1 text-xs w-full justify-center py-1"
          style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
          <Info size={11} /> Légende des couleurs
        </button>

        {showLegend && (
          <div className="mt-2 space-y-1.5 p-3 rounded-xl" style={{ background: 'var(--bg)' }}>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-xs">
                <div className="w-4 h-4 rounded-md flex-shrink-0"
                  style={{ background: v.bg, border: `1px solid ${v.border}` }} />
                <span style={{ color: v.text, fontWeight: 600 }}>{v.label}</span>
                {k === 'en_demande' && (
                  <span style={{ color: 'var(--text-muted)' }}>— devis en cours, non confirmé</span>
                )}
                {k === 'controle_requis' && (
                  <span style={{ color: 'var(--text-muted)' }}>— acompte payé, validation admin</span>
                )}
              </div>
            ))}
            <div className="mt-2 p-2 rounded-lg text-xs" style={{ background: 'rgba(245,158,11,0.08)', color: '#92400E' }}>
              🟠 En demande : Ce produit est dans une demande en attente. La disponibilité finale sera confirmée après validation Show Événement.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
